import { useEffect, useState } from "react";
import api from "@/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, CreditCard } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", description: "", active: true };

export default function PaymentMethods() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/payment-methods");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("name"));
    try {
      if (editing) await api.put(`/payment-methods/${editing.id}`, form);
      else await api.post("/payment-methods", form);
      toast.success(t("saved"));
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const del = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/payment-methods/${id}`);
    load();
  };

  return (
    <div className="space-y-4" data-testid="payment-methods-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-[hsl(var(--primary))]">
          <CreditCard size={18} />
          <h3 className="display-font text-xl font-bold">{t("paymentMethods")}</h3>
        </div>
        <Button onClick={openNew} data-testid="pm-add-btn" className="btn-accent">
          <Plus size={16} className="me-2" /> {t("newPaymentMethod")}
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-10 text-sm text-[hsl(var(--muted-foreground))]">{t("empty")}</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("name")}</TableHead>
              <TableHead>{t("description")}</TableHead>
              <TableHead>{t("status")}</TableHead>
              <TableHead className="text-end">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.id} data-testid={`pm-row-${p.id}`}>
                <TableCell className="font-semibold">{p.name}</TableCell>
                <TableCell className="text-sm text-[hsl(var(--muted-foreground))]">{p.description || "—"}</TableCell>
                <TableCell>
                  <span className={"inline-flex items-center px-2 py-0.5 rounded-full text-xs " +
                    (p.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600")}>
                    {p.active ? t("active") : t("inactive")}
                  </span>
                </TableCell>
                <TableCell className="text-end space-x-1 rtl:space-x-reverse">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`pm-edit-${p.id}`}>
                    <Pencil size={15} />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => del(p.id)} className="text-red-600" data-testid={`pm-del-${p.id}`}>
                    <Trash2 size={15} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? t("editPaymentMethod") : t("addPaymentMethod")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Field label={t("name")}><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pm-name-input" placeholder="Cash, Bank Transfer, STC Pay…" /></Field>
            <Field label={t("description")}><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            <div className="flex items-center justify-between border border-[hsl(var(--border))] rounded-md p-3">
              <Label className="text-sm">{t("active")}</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="pm-active-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} data-testid="pm-save-btn" className="bg-[hsl(var(--primary))] hover:opacity-90 text-white">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase-label">{label}</Label>
    {children}
  </div>
);
